import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Canvas, IText, FabricImage, Rect, Circle, Triangle, Line, loadSVGFromURL, util, type FabricObject } from 'fabric';
import { HexColorPicker } from 'react-colorful';
import QRCode from 'qrcode';
import { track } from '../hooks/usePageTracker';
import { localizedDocsHref } from '../i18n/localizedUrl';
import { api } from '../api/client';
import {
  CANVAS_SIZE, MAX_UNDO, TEMPLATES, BACKGROUND_TEMPLATES, SHAPE_CATEGORIES, RIBBON_CATEGORIES, ICON_NAMES,
  FONT_GROUPS, TEMPLATE_SHAPES, TEMPLATE_STYLES,
  loadGoogleFonts, clampObject, padNum, recolorSvgObject, setupAlignmentGuides,
  downscaleImageDataUrl,
  type TemplateLayer,
} from './badge-designer-utils';

// Fraction of the canvas a template/shape fills when applied — the rest is a
// consistent margin so artwork never crowds the edges. Aspect is preserved.
const TEMPLATE_FIT = 0.9;

// Max serialized design the preview endpoint accepts — kept in lock-step with
// the server's MAX_SVG_BYTES (BWS-BADGES-Badge_preview.mjs) and safely under the
// 6 MB API-Gateway/Lambda sync-invoke request cap. Uploads are downscaled so
// real designs stay far below this.
const PREVIEW_MAX_BYTES = 5 * 1024 * 1024;

// Cap the on-screen design room. The internal canvas is a fixed CANVAS_SIZE
// (450) and is CSS-scaled up to the room; letting the room grow to the full
// editor area (~700px) upscaled the 450px raster ~1.6x and made the badge look
// blurry. Capping near the internal size keeps it crisp (≈1.1x) and smaller.
const MAX_ROOM = 500;

// Tints a silhouette SVG (shape / ribbon / icon) with the selected colour via a
// CSS mask, so every picker thumbnail previews exactly what gets inserted (they
// are all recoloured to the current colour on insert).
const maskSwatch = (url: string, color: string): React.CSSProperties => ({
  backgroundColor: color,
  WebkitMaskImage: `url(${url})`,
  maskImage: `url(${url})`,
  WebkitMaskSize: 'contain',
  maskSize: 'contain',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
  WebkitMaskPosition: 'center',
  maskPosition: 'center',
});

/* ------------------------------------------------------------------ */
/*  Types & handle                                                     */
/* ------------------------------------------------------------------ */

export type PanelId = 'templates' | 'uploads' | 'elements' | 'text' | 'attributes' | 'qr' | 'layers';
type ElementsView = null | 'shapes' | 'icons' | 'ribbons' | 'bases' | 'backgrounds';

export interface BadgeDesignerHandle {
  onExport: () => Promise<string>;
  onExportSVG: () => Promise<string>;
  /** Returns the full Fabric canvas state as a JSON string. The backend
   *  persists this so the editor can rehydrate individual layers later. */
  onExportJSON: () => string;
  /** Load a saved design (Fabric JSON) into the canvas, replacing its content —
   *  used to apply an account template picked in the Templates panel. */
  loadDesign: (json: string) => void;
}

interface BadgeDesignerProps {
  className?: string;
  /** PNG fallback for legacy badges that have no designJson yet. The raster
   *  is loaded as a single non-interactive layer (cannot be edited). */
  initialImageUrl?: string;
  /** Editable Fabric canvas state from a prior save. When present, this
   *  takes precedence over initialImageUrl — individual layers (text,
   *  shapes, icons) are restored as selectable objects. */
  initialDesignJson?: string | null;
  /** D3 — when true and there's no initial content (a fresh badge), open the
   *  template gallery on mount so it's the entry point instead of a blank canvas. */
  autoOpenTemplates?: boolean;
  /** Public "Edit this template" deep-link: apply this template id on mount
   *  (frame + editable layers), as if the user had picked it from the gallery. */
  initialTemplateId?: string;
  /** Rendered beneath the canvas in the third column (the badge details form in
   *  the create flow). Omitted on the public designer. */
  detailsSlot?: React.ReactNode;
  /** Optional right-side header content in the canvas top bar (e.g. Preview,
   *  Private toggle, Save). */
  topBarRight?: React.ReactNode;
  /** The account's saved templates, shown as a "My Templates" source in the
   *  Templates panel. Picking one calls onUseAccountTemplate(id). */
  accountTemplates?: { id: string; name: string; image?: string | null }[];
  onUseAccountTemplate?: (id: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export const BadgeDesigner = forwardRef<BadgeDesignerHandle, BadgeDesignerProps>(function BadgeDesigner({ className, initialImageUrl, initialDesignJson, autoOpenTemplates, initialTemplateId, detailsSlot, topBarRight, accountTemplates, onUseAccountTemplate }, ref) {
  const { t, i18n } = useTranslation('dashboard');
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  // The design room is a SQUARE sized to fill the available editor area; the
  // Fabric canvas fills it 1:1. CANVAS_SIZE (450) stays the LOGICAL coordinate
  // space (object coords, clamping, guides, export) — the display is scaled via
  // canvas.setZoom(displaySize / CANVAS_SIZE), so nothing about geometry changes.
  const roomAreaRef = useRef<HTMLDivElement>(null);
  const [roomSize, setRoomSize] = useState(CANVAS_SIZE);
  const fabricRef = useRef<Canvas | null>(null);
  // Tracks the current background SVG (template/shape) so we can keep it at z=0
  // even after Fabric reshuffles during ActiveSelection dismissal.
  const bgRef = useRef<FabricObject | null>(null);
  // Set when the imported badge image had to be loaded WITHOUT CORS (its host —
  // e.g. an IPFS gateway — sent no Access-Control-Allow-Origin). The image still
  // displays for editing, but the canvas is now tainted, so toDataURL() throws.
  // We remember this + the original URL so onExport() can fall back to it.
  const bgTaintedRef = useRef(false);
  const bgSrcRef = useRef<string | null>(null);
  // Editable placeholder text layers added by a composed template (recognition,
  // …). Tracked so switching templates replaces the previous placeholder set.
  const templateLayersRef = useRef<FabricObject[]>([]);
  // Guards the one-time deep-link template application (initialTemplateId).
  const didInitTemplateRef = useRef(false);

  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedFont, setSelectedFont] = useState('Arial');
  const [selectedFontSize, setSelectedFontSize] = useState(24);
  const [selectedColor, setSelectedColor] = useState('#7c3aed');
  const [showInlineColor, setShowInlineColor] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  // For a fresh badge, land on the Templates panel (the default) so users start
  // from a template instead of a blank canvas.
  useEffect(() => {
    if (autoOpenTemplates && !initialImageUrl && !initialDesignJson && !initialTemplateId) {
      setActivePanel('templates');
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tplFilterStyle, setTplFilterStyle] = useState('');
  const [tplFilterShape, setTplFilterShape] = useState('');
  const [tplFilterPalette, setTplFilterPalette] = useState('');
  const [iconSearch, setIconSearch] = useState('');
  const [catSearch, setCatSearch] = useState(''); // shapes + ribbons category filter
  const [multiplier] = useState(1);
  const [customFonts, setCustomFonts] = useState<string[]>([]);
  // 3-column editor: which left-rail panel is active, the selected object's type
  // (drives the contextual top toolbar), a tick to re-read the Fabric layer list,
  // and the per-session upload library.
  const [activePanel, setActivePanel] = useState<PanelId>('templates');
  const [tplSource, setTplSource] = useState<'library' | 'mine'>('library');
  // On phones the rail is a bottom bar and the panel is a collapsible sheet.
  const [mobilePanelOpen, setMobilePanelOpen] = useState(true);
  const [activeType, setActiveType] = useState<string | null>(null);
  const [layersTick, setLayersTick] = useState(0);
  // True when the design contains at least one merge token — gates the Preview.
  const hasTokens = useMemo(() => {
    const c = fabricRef.current;
    return !!c && c.getObjects().some((o) => /\{\{[a-zA-Z_.]+\}\}/.test((o as { text?: string }).text || ''));
  }, [layersTick]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [previewTooLarge, setPreviewTooLarge] = useState(false);
  const [uploads, setUploads] = useState<string[]>([]);
  const [elementsView, setElementsView] = useState<ElementsView>(null);
  const [bgShape, setBgShape] = useState('');

  // Undo / redo
  const undoStack = useRef<string[]>([]);
  const redoStack = useRef<string[]>([]);
  const skipSave = useRef(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Popover anchor refs
  const colorPickerRef = useRef<HTMLDivElement>(null);

  /* ---------- History ---------- */

  const saveState = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas || skipSave.current) return;
    undoStack.current.push(JSON.stringify(canvas.toJSON()));
    if (undoStack.current.length > MAX_UNDO) undoStack.current.shift();
    redoStack.current = [];
    setCanUndo(undoStack.current.length > 1); // >1 means there's a prior state to return to
    setCanRedo(false);
  }, []);

  const restoreState = useCallback((json: string) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    skipSave.current = true;
    canvas.loadFromJSON(json).then(() => { canvas.renderAll(); skipSave.current = false; });
  }, []);

  const undo = useCallback(() => {
    const canvas = fabricRef.current;
    // Top of the stack is the current committed state; we need a prior one.
    if (!canvas || undoStack.current.length < 2) return;
    track('designer_undo');
    redoStack.current.push(undoStack.current.pop()!);            // current -> redo
    restoreState(undoStack.current[undoStack.current.length - 1]); // restore the previous state
    setCanUndo(undoStack.current.length > 1);
    setCanRedo(true);
  }, [restoreState]);

  const redo = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas || redoStack.current.length === 0) return;
    track('designer_redo');
    const state = redoStack.current.pop()!;
    undoStack.current.push(state);                              // becomes the current state again
    restoreState(state);
    setCanUndo(undoStack.current.length > 1);
    setCanRedo(redoStack.current.length > 0);
  }, [restoreState]);

  /* ---------- Canvas init ---------- */

  useEffect(() => {
    loadGoogleFonts();
    const canvas = new Canvas(canvasElRef.current!, {
      width: CANVAS_SIZE, height: CANVAS_SIZE, backgroundColor: '#ffffff'
    });
    fabricRef.current = canvas;
    // E2E-only inspection hook: lets Playwright assert on real Fabric state
    // (object count/types/positions) instead of opaque canvas pixels — which is
    // what makes headless canvas tests reliable. Guarded so it's inert in prod
    // unless a test explicitly opts in via window.__E2E__.
    const w = window as unknown as { __E2E__?: boolean; __designer?: unknown };
    if (import.meta.env.MODE !== 'production' || w.__E2E__) {
      w.__designer = {
        count: () => canvas.getObjects().length,
        types: () => canvas.getObjects().map((o) => o.type),
        json: () => canvas.toJSON(),
        active: () => {
          const a = canvas.getActiveObject() as (FabricObject & { fill?: unknown; fontFamily?: string; text?: string }) | undefined;
          return a ? { type: a.type, left: Math.round(a.left ?? 0), top: Math.round(a.top ?? 0), fill: a.fill, fontFamily: a.fontFamily, text: a.text } : null;
        },
        // e2e-only precise mutators (docs snapshots): find a text object's geometry,
        // remove it, or reposition/retext the active object — so an added heading
        // lands cleanly in place of a placeholder instead of overlapping the badge.
        find: (substr: string) => {
          const m = substr.toUpperCase();
          const o = canvas.getObjects().find((x) => (x.type === 'i-text' || x.type === 'text') && String((x as unknown as { text?: string }).text || '').toUpperCase().includes(m)) as (FabricObject & { fontSize?: number; originX?: string; originY?: string; fill?: unknown; fontWeight?: unknown; charSpacing?: number }) | undefined;
          return o ? { left: o.left, top: o.top, fontSize: o.fontSize, originX: o.originX, originY: o.originY, fill: o.fill, fontWeight: o.fontWeight, charSpacing: o.charSpacing } : null;
        },
        remove: (substr: string) => {
          const m = substr.toUpperCase();
          const o = canvas.getObjects().find((x) => (x.type === 'i-text' || x.type === 'text') && String((x as unknown as { text?: string }).text || '').toUpperCase().includes(m));
          if (o) { canvas.remove(o); canvas.requestRenderAll(); return true; }
          return false;
        },
        setActive: (props: Record<string, unknown>) => {
          const a = canvas.getActiveObject();
          if (!a) return false;
          a.set(props);
          a.setCoords();
          canvas.requestRenderAll();
          return true;
        },
        // e2e-only layer-boundary inspection (verify-template-layers.mjs): the
        // frame's rendered bbox, each applied template layer's bbox, and a toggle
        // to hide the layers so the checker can capture the frame silhouette alone
        // and assert every layer pixel sits inside it with margin.
        bgBox: () => (bgRef.current ? bgRef.current.getBoundingRect() : null),
        layerBoxes: () =>
          templateLayersRef.current.map((o) => ({
            type: o.type,
            text: (o as unknown as { text?: string }).text ?? null,
            ...o.getBoundingRect(),
          })),
        setLayersVisible: (v: boolean) => {
          for (const o of templateLayersRef.current) o.visible = v;
          canvas.requestRenderAll();
        },
      };
    }
    const keepBgAtBack = () => {
      const bg = bgRef.current;
      if (bg && canvas.getObjects().includes(bg)) {
        canvas.sendObjectToBack(bg);
      }
    };
    const onSelect = () => {
      setHasSelection(true);
      const a = canvas.getActiveObject() as (FabricObject & { fontFamily?: string; fontSize?: number; scaleY?: number }) | undefined;
      setActiveType((a?.type as string) ?? null);
      // Mirror the selected text's font + effective size into the toolbar so the
      // stepper starts from the object's real value.
      if (a && (a.type === 'i-text' || a.type === 'text')) {
        if (a.fontFamily) setSelectedFont(a.fontFamily);
        if (a.fontSize) setSelectedFontSize(Math.round(a.fontSize * (a.scaleY ?? 1)));
      }
    };
    const bumpLayers = () => setLayersTick((n) => n + 1);
    canvas.on('selection:created', onSelect);
    canvas.on('selection:updated', onSelect);
    canvas.on('selection:cleared', () => { setHasSelection(false); setActiveType(null); keepBgAtBack(); canvas.requestRenderAll(); });
    canvas.on('object:added', () => { keepBgAtBack(); bumpLayers(); });
    canvas.on('object:moving', (e) => clampObject(e.target));
    canvas.on('object:scaling', (e) => clampObject(e.target));
    canvas.on('object:added', () => saveState());
    canvas.on('object:removed', () => { saveState(); bumpLayers(); });
    canvas.on('object:modified', () => { saveState(); bumpLayers(); });
    setupAlignmentGuides(canvas);
    // Seed the undo history with the initial (empty) canvas. The stack invariant
    // is "top === current committed state", so undo pops the current state and
    // restores the previous one. Without this baseline the first edit couldn't be
    // undone, and undo would restore the just-saved (current) state — a no-op.
    undoStack.current = [JSON.stringify(canvas.toJSON())];
    return () => { canvas.dispose(); fabricRef.current = null; delete w.__designer; };
  }, [saveState]);

  /* ---------- Responsive square room ---------- */
  // Size the square design room + canvas to the largest square that fits the
  // available editor area, and re-fit on any resize. Logical space stays 450 via
  // zoom, so a template that fills the canvas now fills the whole editing room.
  useEffect(() => {
    const area = roomAreaRef.current;
    const canvas = fabricRef.current;
    if (!area || !canvas) return;
    const fit = () => {
      const c = fabricRef.current;
      if (!c) return;
      // Width-driven (the canvas column scrolls vertically), capped for crispness.
      const size = Math.min(MAX_ROOM, Math.max(280, Math.floor(area.clientWidth)));
      if (!size || !isFinite(size)) return;
      setRoomSize(size);
      // CSS-ONLY scale: the internal canvas stays CANVAS_SIZE (450) — its logical
      // coordinate space at zoom 1 — so ALL geometry (template centering, its
      // pixel-correction pass, object clamping, alignment guides, export) is
      // unchanged; only the DISPLAYED size grows to fill the square room. Using
      // setZoom here instead breaks the getBoundingRect-based template centering
      // and the lowerCanvasEl pixel pass, which both assume zoom 1.
      c.setDimensions({ width: size, height: size }, { cssOnly: true });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(area);
    return () => ro.disconnect();
  }, []);

  /* ---------- Keyboard shortcuts ---------- */

  useEffect(() => {
    const isEditableTarget = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
    };
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'Z' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't hijack Delete/Backspace when the user is typing in any form
        // field outside the canvas — otherwise editing a badge name/desc/etc.
        // would silently delete the currently-selected canvas element.
        if (isEditableTarget()) return;
        const active = fabricRef.current?.getActiveObject();
        if (active && active.selectable && !(active instanceof IText && (active as IText).isEditing)) {
          fabricRef.current!.remove(active);
          fabricRef.current!.discardActiveObject();
          fabricRef.current!.renderAll();
        }
      }
      if (e.key === 'Escape') {
        // Intentional deselect. Replaces the old pointer-leave deselect, which
        // yanked the selection the instant you moved toward the toolbar to
        // style the selected element. Fabric handles Esc-exits-text-editing.
        const c = fabricRef.current;
        const active = c?.getActiveObject();
        if (active && !(active instanceof IText && (active as IText).isEditing)) {
          c!.discardActiveObject();
          c!.requestRenderAll();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  /* ---------- Initial state (designJson preferred, PNG fallback) ----------
     New badges: rehydrate from the saved Fabric JSON so individual layers
     stay editable. Legacy badges (no designJson yet) fall back to importing
     the rendered IPFS PNG as a single non-interactive raster — same
     behavior as before. */

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    if (initialDesignJson) {
      skipSave.current = true;
      canvas.loadFromJSON(initialDesignJson)
        .then(() => { canvas.renderAll(); skipSave.current = false; })
        .catch(() => { skipSave.current = false; });
      return;
    }
    if (!initialImageUrl) return;
    const url = initialImageUrl;
    bgSrcRef.current = url;

    const place = (img: FabricImage, tainted: boolean) => {
      const c = fabricRef.current;
      if (!c) return;
      c.clear(); c.backgroundColor = '#ffffff';
      // Uniform fit-within (preserve aspect ratio), then center — the old code
      // called scaleToWidth AND scaleToHeight, stretching non-square images.
      img.scaleToWidth(CANVAS_SIZE);
      if (img.getScaledHeight() > CANVAS_SIZE) img.scaleToHeight(CANVAS_SIZE);
      img.set({
        left: (CANVAS_SIZE - img.getScaledWidth()) / 2,
        top: (CANVAS_SIZE - img.getScaledHeight()) / 2,
        selectable: false, evented: false,
      });
      c.add(img); c.sendObjectToBack(img); c.renderAll();
      // Track the imported raster as the background so a later loadSvgBackground()
      // (template change) actually removes it.
      bgRef.current = img;
      bgTaintedRef.current = tainted;
    };

    // Prefer a CORS load (keeps the canvas exportable via toDataURL). If the host
    // grants no CORS the promise rejects — fall back to a plain load so the badge
    // at least DISPLAYS for editing (canvas becomes tainted; export handles it).
    FabricImage.fromURL(url, { crossOrigin: 'anonymous' })
      .then((img) => place(img, false))
      .catch(() => {
        FabricImage.fromURL(url).then((img) => place(img, true)).catch(() => {});
      });
  }, [initialImageUrl, initialDesignJson]);

  /* ---------- Close color picker on outside click ---------- */

  useEffect(() => {
    if (!showColorPicker) return;
    const handler = (e: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) setShowColorPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColorPicker]);

  /* ---------- SVG loaders ---------- */

  const loadSvgBackground = useCallback(async (url: string, color?: string) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    // Replace only the previous background; preserve foreground objects
    // (icons, text, ribbons, uploaded images).
    if (bgRef.current && canvas.getObjects().includes(bgRef.current)) {
      canvas.remove(bgRef.current);
    }
    bgRef.current = null;
    canvas.backgroundColor = '#ffffff';
    try {
      const { objects, options } = await loadSVGFromURL(url);
      const group = util.groupSVGElements(objects as FabricObject[], options);
      // Add to canvas first so Fabric's transform matrix is initialized,
      // then use scaleToWidth/Height (Fabric's idiomatic helpers) to fit-within.
      canvas.add(group);
      // Fit within a FRACTION of the canvas so the artwork keeps a consistent
      // margin instead of crowding the edges. Aspect ratio is always preserved
      // (scaleToWidth/Height + refit all scale X and Y by the same factor) — a
      // near-square hexagon whose points touched all four edges read as
      // "squished"; the margin is what makes every template sit cleanly.
      const FIT = CANVAS_SIZE * TEMPLATE_FIT;
      group.scaleToWidth(FIT);
      if (group.getScaledHeight() > FIT) group.scaleToHeight(FIT);
      // Initial center via Fabric's geometric bbox.
      const bbox = group.getBoundingRect();
      const refit = Math.min(FIT / bbox.width, FIT / bbox.height);
      group.set({ scaleX: (group.scaleX || 1) * refit, scaleY: (group.scaleY || 1) * refit });
      group.setCoords();
      const bbox2 = group.getBoundingRect();
      group.set({
        left: (group.left || 0) + (CANVAS_SIZE - bbox2.width) / 2 - bbox2.left,
        top: (group.top || 0) + (CANVAS_SIZE - bbox2.height) / 2 - bbox2.top,
        // Background is non-interactive: foreground objects own all clicks.
        // Recolor via the popover ColorBar (pre-color) or by re-selecting the
        // shape in a new color.
        selectable: false, evented: false
      });
      group.setCoords();
      if (color) recolorSvgObject(group, color);
      canvas.sendObjectToBack(group);
      bgRef.current = group;
      canvas.renderAll();

      // Pixel-correction pass: Fabric's geometric bbox can include invisible
      // padding (transparent shapes, white-filled rects) extending past the
      // visible content. Measure the actual rendered pixels and shift the group
      // so the visible content is centered.
      // Only the background frame should drive centering — hide every other
      // object (template placeholder text, user text/icons) during the pixel
      // measure, otherwise their pixels widen the bbox and shove the frame into
      // a corner (regression seen when switching between composed templates).
      const others = canvas.getObjects().filter((o) => o !== group && o.visible !== false);
      if (others.length) { for (const o of others) o.visible = false; canvas.renderAll(); }
      const lc = (canvas as unknown as { lowerCanvasEl: HTMLCanvasElement }).lowerCanvasEl;
      const ctx = lc?.getContext('2d', { willReadFrequently: true });
      if (ctx) {
        const data = ctx.getImageData(0, 0, lc.width, lc.height).data;
        let minX = lc.width, minY = lc.height, maxX = -1, maxY = -1;
        for (let y = 0; y < lc.height; y++) {
          for (let x = 0; x < lc.width; x++) {
            const i = (y * lc.width + x) * 4;
            const a = data[i + 3];
            if (a >= 16 && (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250)) {
              if (x < minX) minX = x;
              if (y < minY) minY = y;
              if (x > maxX) maxX = x;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX >= 0) {
          // The bitmap is in DEVICE pixels: with retina scaling the backing
          // store is CANVAS_SIZE × devicePixelRatio (e.g. 900 at DPR 2). Center
          // in that device space, then convert the shift back to logical units
          // (÷dpr) before moving the group — otherwise DPR>1 canvases get a huge
          // bogus offset that flings the frame into a corner.
          const dpr = lc.width / CANVAS_SIZE || 1;
          const visW = maxX - minX + 1;
          const visH = maxY - minY + 1;
          const dx = ((lc.width - visW) / 2 - minX) / dpr;
          const dy = ((lc.height - visH) / 2 - minY) / dpr;
          if (Math.abs(dx) >= 1 || Math.abs(dy) >= 1) {
            group.set({ left: (group.left || 0) + dx, top: (group.top || 0) + dy });
            group.setCoords();
          }
        }
      }
      if (others.length) { for (const o of others) o.visible = true; }
      canvas.renderAll();
    } catch { /* SVG load failed */ }
  }, []);

  const addSvgElement = useCallback(async (url: string, color?: string) => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    try {
      const { objects, options } = await loadSVGFromURL(url);
      const group = util.groupSVGElements(objects as FabricObject[], options);
      const scale = Math.min(120 / group.width!, 120 / group.height!);
      group.set({ scaleX: scale, scaleY: scale, left: CANVAS_SIZE / 2 - (group.width! * scale) / 2, top: CANVAS_SIZE / 2 - (group.height! * scale) / 2 });
      if (color) recolorSvgObject(group, color);
      canvas.add(group); canvas.setActiveObject(group); canvas.renderAll();
    } catch { /* SVG load failed */ }
  }, []);

  /* ---------- Handlers ---------- */

  // Add a composed template's placeholder text as separate editable/deletable
  // layers on top of the frame. Replaces any placeholders from a prior template.
  const applyTemplateLayers = useCallback((layers?: TemplateLayer[]) => {
    const c = fabricRef.current;
    if (!c) return;
    for (const o of templateLayersRef.current) {
      if (c.getObjects().includes(o)) c.remove(o);
    }
    templateLayersRef.current = [];
    // Icon/shape layers load their SVG asynchronously; collect the promises so
    // we snapshot undo state only once every layer is on the canvas.
    const pending: Promise<void>[] = [];
    // Load an SVG asset (icon/shape/ribbon) as an independent, editable object,
    // scaled to `size`, centred at (left, top), recoloured, and tracked as a
    // template layer so the next template swap removes it cleanly.
    const addSvgLayer = (url: string, size: number, left: number, top: number, fill?: string) => {
      const p = loadSVGFromURL(url)
        .then(({ objects, options }) => {
          const cc = fabricRef.current;
          if (!cc) return;
          const group = util.groupSVGElements(objects as FabricObject[], options);
          const scale = Math.min(size / (group.width || size), size / (group.height || size));
          group.set({ originX: 'center', originY: 'center', left, top, scaleX: scale, scaleY: scale });
          if (fill) recolorSvgObject(group, fill);
          cc.add(group);
          // Sit just above the (back-most) frame so placeholder text stays on top.
          cc.sendObjectToBack(group);
          if (bgRef.current) cc.bringObjectForward(group);
          templateLayersRef.current.push(group);
          cc.requestRenderAll();
        })
        .catch(() => { /* asset load failed — skip this layer */ });
      pending.push(p);
    };
    if (layers?.length) {
      for (const L of layers) {
        if (L.type === 'text') {
          const it = new IText(L.text, {
            left: L.left ?? CANVAS_SIZE / 2,
            top: L.top,
            originX: 'center',
            originY: 'center',
            textAlign: 'center',
            fontSize: L.fontSize,
            fontFamily: L.fontFamily,
            fill: L.fill,
            fontWeight: L.fontWeight ?? 'normal',
            charSpacing: L.charSpacing ?? 0,
          });
          c.add(it);
          templateLayersRef.current.push(it);
        } else if (L.type === 'icon') {
          addSvgLayer(`/assets/badge-icons/icon-${L.icon}.svg`, L.size, L.left ?? CANVAS_SIZE / 2, L.top, L.fill);
        } else if (L.type === 'shape') {
          const dir = L.kind === 'ribbon' ? 'badge-ribbons/ribbon' : 'badge-shapes/shape';
          addSvgLayer(`/assets/${dir}-${padNum(L.num)}.svg`, L.size, L.left ?? CANVAS_SIZE / 2, L.top, L.fill);
        }
      }
    }
    c.discardActiveObject();
    c.requestRenderAll();
    // Webfonts (Montserrat, …) may still be loading — Fabric renders the layers
    // in a fallback serif and won't re-measure when the font arrives. Re-render
    // (marking each layer dirty so its metrics recompute) once fonts are ready.
    if (templateLayersRef.current.length && typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => {
        const cc = fabricRef.current;
        if (!cc) return;
        for (const o of templateLayersRef.current) o.set('dirty', true);
        cc.requestRenderAll();
      });
    }
    // Snapshot undo state after all async layers have landed (or immediately if
    // there are none), so a single undo clears the whole applied template.
    if (pending.length) Promise.allSettled(pending).then(() => saveState());
    else saveState();
  }, [saveState]);

  const handleTemplateSelect = (id: string) => {
    track('designer_template_select', { id: id || 'none' });
    setSelectedTemplate(id);
    if (!id) {
      const c = fabricRef.current;
      if (c) {
        if (bgRef.current && c.getObjects().includes(bgRef.current)) c.remove(bgRef.current);
        bgRef.current = null;
        applyTemplateLayers();
        c.backgroundColor = '#ffffff';
        c.renderAll();
      }
      return;
    }
    // Look in both the composed Templates and the layer-less Background shapes.
    const tpl = TEMPLATES.find((t) => t.id === id) ?? BACKGROUND_TEMPLATES.find((t) => t.id === id);
    if (tpl) loadSvgBackground(tpl.svg).then(() => applyTemplateLayers(tpl.layers));
  };

  // Deep-link: apply initialTemplateId to the CURRENT canvas (public "Edit this
  // template" flow). The guard is reset on cleanup so React 18 StrictMode's
  // dev-only mount→unmount→remount re-applies to the freshly created canvas
  // (otherwise the frame lands on the disposed canvas and only the layers, which
  // re-read the live canvas, survive). Production mounts once.
  useEffect(() => {
    if (!initialTemplateId || didInitTemplateRef.current) return;
    if (!fabricRef.current) return;
    const tpl = TEMPLATES.find((t) => t.id === initialTemplateId) ?? BACKGROUND_TEMPLATES.find((t) => t.id === initialTemplateId);
    if (!tpl) return;
    didInitTemplateRef.current = true;
    setSelectedTemplate(tpl.id);
    loadSvgBackground(tpl.svg).then(() => applyTemplateLayers(tpl.layers));
    return () => { didInitTemplateRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplateId, loadSvgBackground, applyTemplateLayers]);

  // Shapes are ADDED as editable, selectable components (like ribbons/icons) —
  // smaller than the canvas and on top of any template — not set as the
  // non-interactive background. Recoloured to the current colour on insert.
  const handleShapeSelect = (num: number) => { track('designer_shape_add', { n: String(num) }); addSvgElement(`/assets/badge-shapes/shape-${padNum(num)}.svg`, selectedColor); };
  const handleRibbonSelect = (num: number) => { track('designer_ribbon_add', { n: String(num) }); addSvgElement(`/assets/badge-ribbons/ribbon-${padNum(num)}.svg`, selectedColor); };
  const handleIconSelect = (name: string) => { track('designer_icon_add', { name }); addSvgElement(`/assets/badge-icons/icon-${name}.svg`, selectedColor); };

  const handleUploadBg = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !fabricRef.current) return;
    const fmt = (file.type.split('/')[1] || '?').replace('+xml', '');
    if (file.size > 5 * 1024 * 1024) { track('designer_image_upload', { format: fmt, ok: 'false', reason: 'too_large' }); alert(t('designer.fileTooLarge', 'File must be under 5 MB')); return; }
    if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) { track('designer_image_upload', { format: fmt, ok: 'false', reason: 'bad_format' }); alert(t('designer.invalidFormat', 'Only PNG, JPEG, and SVG are supported')); return; }
    track('designer_image_upload', { format: fmt, ok: 'true' });
    const reader = new FileReader();
    reader.onload = async () => {
      const canvas = fabricRef.current!;
      try {
        // Shrink full-resolution uploads before they enter the canvas: embedded
        // base64 images otherwise bloat the design past the 6 MB Lambda payload
        // limit and silently break Preview/Create (HTTP 413). Best-effort.
        const src = await downscaleImageDataUrl(reader.result as string);
        const img = await FabricImage.fromURL(src);
        // Insert as a selectable element at ~40% of canvas, centered.
        const target = CANVAS_SIZE * 0.4;
        const scale = Math.min(target / img.width!, target / img.height!);
        img.set({
          scaleX: scale, scaleY: scale,
          left: CANVAS_SIZE / 2 - (img.width! * scale) / 2,
          top: CANVAS_SIZE / 2 - (img.height! * scale) / 2
        });
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        // Remember the (downscaled) upload so the Uploads panel can re-insert it.
        setUploads((prev) => (prev.includes(src) ? prev : [src, ...prev]));
      } catch { /* Image load failed */ }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Re-insert an already-uploaded image (from the Uploads "All Used" library).
  const insertUpload = async (dataUrl: string) => {
    const canvas = fabricRef.current; if (!canvas) return;
    try {
      const img = await FabricImage.fromURL(dataUrl);
      const target = CANVAS_SIZE * 0.4;
      const scale = Math.min(target / img.width!, target / img.height!);
      img.set({ scaleX: scale, scaleY: scale, left: CANVAS_SIZE / 2 - (img.width! * scale) / 2, top: CANVAS_SIZE / 2 - (img.height! * scale) / 2 });
      canvas.add(img); canvas.setActiveObject(img); canvas.renderAll();
    } catch { /* ignore */ }
  };

  const applyFont = (font: string) => {
    track('designer_font_change');
    setSelectedFont(font);
    const active = fabricRef.current?.getActiveObject();
    if (active?.type === 'i-text') {
      (active as IText).set('fontFamily', font);
      fabricRef.current!.renderAll();
      // Most families load on demand, so the first render can flash a fallback.
      // Fetch the actual face, then re-render once it's ready.
      if (typeof document !== 'undefined' && document.fonts?.load) {
        document.fonts.load(`16px "${font}"`).then(() => fabricRef.current?.requestRenderAll()).catch(() => {});
      }
    }
  };

  // Resize the selected text via the +/- stepper (or direct entry) instead of
  // dragging its handles. Bakes any prior corner-scale into fontSize so the
  // displayed value stays the single source of truth.
  const changeFontSize = (next: number | ((cur: number) => number)) => {
    const active = fabricRef.current?.getActiveObject() as (IText & { scaleX?: number; scaleY?: number }) | undefined;
    if (!active || (active.type !== 'i-text' && active.type !== 'text')) return;
    const cur = Math.round((active.fontSize ?? 24) * (active.scaleY ?? 1));
    const size = Math.min(200, Math.max(6, Math.round(typeof next === 'function' ? next(cur) : next)));
    active.set({ fontSize: size, scaleX: 1, scaleY: 1 });
    active.setCoords();
    fabricRef.current!.requestRenderAll();
    setSelectedFontSize(size);
    track('designer_font_size_change');
  };

  const handleFontUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    track('designer_font_upload');
    const fontName = file.name.replace(/\.(ttf|woff|woff2|otf)$/i, '');
    const reader = new FileReader();
    reader.onload = () => {
      const style = document.createElement('style');
      style.textContent = `@font-face { font-family: '${fontName}'; src: url('${reader.result as string}'); }`;
      document.head.appendChild(style);
      setCustomFonts((prev) => prev.includes(fontName) ? prev : [...prev, fontName]);
      applyFont(fontName);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const applyColor = (color: string) => {
    setSelectedColor(color);
    const active = fabricRef.current?.getActiveObject();
    if (!active) return;
    track('designer_color_change');
    if (active.type === 'i-text') (active as IText).set('fill', color);
    else recolorSvgObject(active, color);
    fabricRef.current!.renderAll();
  };

  // Align the active object (single, or an ActiveSelection of many) to the badge canvas.
  // Works uniformly for both because Fabric exposes a multi-selection as one object
  // with a combined bounding rect.
  const alignH = (mode: 'left' | 'center' | 'right') => {
    const canvas = fabricRef.current;
    const obj = canvas?.getActiveObject();
    if (!canvas || !obj) return;
    track('designer_align', { dir: mode });
    const bbox = obj.getBoundingRect();
    const dx =
      mode === 'left'  ? -bbox.left :
      mode === 'right' ? CANVAS_SIZE - (bbox.left + bbox.width) :
                         (CANVAS_SIZE - bbox.width) / 2 - bbox.left;
    obj.set({ left: (obj.left || 0) + dx });
    obj.setCoords();
    canvas.renderAll();
  };

  const alignV = (mode: 'top' | 'middle' | 'bottom') => {
    const canvas = fabricRef.current;
    const obj = canvas?.getActiveObject();
    if (!canvas || !obj) return;
    track('designer_align', { dir: mode });
    const bbox = obj.getBoundingRect();
    const dy =
      mode === 'top'    ? -bbox.top :
      mode === 'bottom' ? CANVAS_SIZE - (bbox.top + bbox.height) :
                          (CANVAS_SIZE - bbox.height) / 2 - bbox.top;
    obj.set({ top: (obj.top || 0) + dy });
    obj.setCoords();
    canvas.renderAll();
  };

  const deleteSelected = () => {
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    if (active?.selectable) { track('designer_delete'); canvas!.remove(active); canvas!.discardActiveObject(); canvas!.renderAll(); }
  };

  /* ---------- Export ---------- */

  // Export is always from the fixed 450 internal resolution (the display is
  // CSS-scaled only, zoom stays 1) — deterministic regardless of screen size.
  const getBase64 = useCallback(() => {
    const c = fabricRef.current; if (!c) return '';
    c.discardActiveObject(); c.renderAll();
    // Export on a TRANSPARENT background. Credly, Certifier, and the Open Badges
    // visual guidelines all require transparent PNGs so the badge composites
    // onto any page/email background instead of showing a white box. The editing
    // surface stays white (dropped only for the pixel readback, then restored).
    const editBg = c.backgroundColor;
    try {
      c.backgroundColor = '';
      c.renderAll();
      return c.toDataURL({ format: 'png', multiplier });
    } catch {
      // Tainted canvas (a cross-origin badge image loaded without CORS). We can't
      // read the pixels back — keep the badge's existing baked image so saving
      // still succeeds instead of throwing.
      return bgSrcRef.current ?? '';
    } finally {
      c.backgroundColor = editBg;
      c.renderAll();
    }
  }, [multiplier]);

  const getSVG = useCallback(() => {
    const c = fabricRef.current; if (!c) return '';
    c.discardActiveObject(); c.renderAll();
    // Fabric prepends an <?xml?> declaration + <!DOCTYPE> before <svg>. The
    // preview endpoint validates that the document STARTS at <svg>, so strip the
    // preamble — otherwise every designer preview 400s as "not an SVG document".
    return c.toSVG()
      .replace(/^\s*<\?xml[^>]*\?>\s*/i, '')
      .replace(/^\s*<!DOCTYPE[^>]*>\s*/i, '');
  }, []);

  // Render the design with SAMPLE merge data via the SAME server path used at
  // issuance, so the issuer sees exactly how tokens will look for a recipient.
  const handlePreview = useCallback(async () => {
    setPreviewError(false); setPreviewTooLarge(false); setPreviewLoading(true); setPreviewImage('');
    const svg = getSVG();
    const bytes = new Blob([svg]).size;
    // The render goes through a synchronous Lambda invoke, capped at 6 MB request
    // body by API Gateway; the server (Badge_preview) also rejects > PREVIEW_MAX.
    // Short-circuit an oversized design with a clear message instead of a doomed
    // round-trip that comes back as a generic "cannot render". Kept in lock-step
    // with the server guard so the client catches it first.
    if (bytes > PREVIEW_MAX_BYTES) {
      track('badge_preview_error', { status: 'presend_too_large', bytes: String(bytes) });
      setPreviewTooLarge(true); setPreviewLoading(false);
      return;
    }
    try {
      const res = await api.previewBadgeImage(svg);
      setPreviewImage(res.image);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      const msg = (e as { message?: string })?.message ?? '';
      // 413 = gateway payload cap; 400 "too large" = the server guard. Both mean
      // the design is too big — show the actionable message, not the generic one.
      const tooLarge = status === 413 || (status === 400 && /too large/i.test(msg));
      track('badge_preview_error', { status: String(status ?? 'network'), bytes: String(bytes) });
      setPreviewTooLarge(tooLarge); setPreviewError(!tooLarge);
    }
    finally { setPreviewLoading(false); }
  }, [getSVG]);

  const getJSON = useCallback(() => {
    const c = fabricRef.current; if (!c) return '';
    // Discard the active selection so the serialized state doesn't carry an
    // ActiveSelection wrapper around the user's selected objects.
    c.discardActiveObject(); c.renderAll();
    return JSON.stringify(c.toJSON());
  }, []);

  const loadDesign = useCallback((json: string) => {
    const c = fabricRef.current; if (!c || !json) return;
    skipSave.current = true;
    bgRef.current = null;
    setSelectedTemplate('');
    c.loadFromJSON(json)
      .then(() => { c.renderAll(); skipSave.current = false; setLayersTick((n) => n + 1); saveState(); })
      .catch(() => { skipSave.current = false; });
  }, [saveState]);

  useImperativeHandle(ref, () => ({
    onExport: () => Promise.resolve(getBase64()),
    onExportSVG: () => Promise.resolve(getSVG()),
    onExportJSON: () => getJSON(),
    loadDesign,
  }), [getBase64, getSVG, getJSON, loadDesign]);

  /* ---------- Panel actions (3-column editor) ---------- */

  const addStyledText = (text: string, fontSize: number, fontWeight: string = 'normal') => {
    const c = fabricRef.current; if (!c) return;
    track('designer_text_add');
    const it = new IText(text, {
      left: CANVAS_SIZE / 2, top: CANVAS_SIZE / 2, originX: 'center', originY: 'center',
      textAlign: 'center', fontFamily: selectedFont, fill: selectedColor, fontSize, fontWeight,
    });
    c.add(it); c.setActiveObject(it); c.requestRenderAll();
  };
  const addHeading = () => addStyledText(t('designer.headingText', 'Heading'), 44, '800');
  const addSubheading = () => addStyledText(t('designer.subheadingText', 'Subheading'), 26, '600');
  const addNormalText = () => addStyledText(t('designer.bodyText', 'Your text'), 18, 'normal');

  // Insert a dynamic attribute token (resolved per award at render/share time).
  const insertToken = (token: string) => addStyledText(token, 18, '600');

  // Insert a ready-made stack of text lines (a "combination"), centred and
  // stacked vertically — each line stays an independently editable object.
  const addCombination = (lines: { text: string; size: number; weight?: string }[]) => {
    const c = fabricRef.current; if (!c) return;
    track('designer_combination_add');
    const gap = 8;
    const totalH = lines.reduce((s, l) => s + l.size, 0) + gap * (lines.length - 1);
    let y = CANVAS_SIZE / 2 - totalH / 2;
    let last: FabricObject | null = null;
    for (const l of lines) {
      const it = new IText(l.text, {
        left: CANVAS_SIZE / 2, top: y + l.size / 2, originX: 'center', originY: 'center',
        textAlign: 'center', fontFamily: selectedFont, fill: selectedColor, fontSize: l.size, fontWeight: l.weight ?? 'normal',
      });
      c.add(it); last = it; y += l.size + gap;
    }
    if (last) c.setActiveObject(last);
    c.requestRenderAll();
  };

  const addLine = (orientation: 'h' | 'v') => {
    const c = fabricRef.current; if (!c) return;
    const half = CANVAS_SIZE / 2, len = CANVAS_SIZE * 0.5;
    const coords: [number, number, number, number] = orientation === 'h'
      ? [half - len / 2, half, half + len / 2, half]
      : [half, half - len / 2, half, half + len / 2];
    const line = new Line(coords, { stroke: selectedColor, strokeWidth: 4, strokeLineCap: 'round' });
    c.add(line); c.setActiveObject(line); c.requestRenderAll();
  };

  const addGenericShape = (kind: 'rect' | 'roundRect' | 'circle' | 'triangle', filled: boolean) => {
    const c = fabricRef.current; if (!c) return;
    const size = 110, mid = CANVAS_SIZE / 2;
    const common = filled
      ? { fill: selectedColor }
      : { fill: 'transparent', stroke: selectedColor, strokeWidth: 4 };
    const base = { left: mid, top: mid, originX: 'center' as const, originY: 'center' as const, ...common };
    let obj: FabricObject;
    if (kind === 'circle') obj = new Circle({ ...base, radius: size / 2 });
    else if (kind === 'triangle') obj = new Triangle({ ...base, width: size, height: size });
    else obj = new Rect({ ...base, width: size, height: size, rx: kind === 'roundRect' ? 18 : 0, ry: kind === 'roundRect' ? 18 : 0 });
    c.add(obj); c.setActiveObject(obj); c.requestRenderAll();
  };

  // QR code — a scannable code for the award's verification page. In the editor
  // it encodes a preview URL in the selected colour; the real per-recipient code
  // is generated at award time. Marked with an award-url tag so a future award
  // pipeline can regenerate it. Inserted as a selectable, aspect-locked image.
  const addQr = async () => {
    const c = fabricRef.current; if (!c) return;
    try {
      const dataUrl = await QRCode.toDataURL('https://badges.ninja/verify/preview', {
        margin: 1, width: 240, color: { dark: selectedColor, light: '#00000000' },
      });
      const img = await FabricImage.fromURL(dataUrl);
      const target = CANVAS_SIZE * 0.28;
      const scale = target / img.width!;
      img.set({ scaleX: scale, scaleY: scale, left: CANVAS_SIZE / 2 - target / 2, top: CANVAS_SIZE / 2 - target / 2, lockUniScaling: true });
      (img as unknown as { bwsQr?: string }).bwsQr = '{{award_url}}';
      c.add(img); c.setActiveObject(img); c.requestRenderAll();
    } catch { /* ignore */ }
  };

  // Layer panel — reads through layersTick so the list re-renders on canvas change.
  void layersTick;
  const getLayers = (): FabricObject[] => {
    const c = fabricRef.current; if (!c) return [];
    // Top of the panel = front of the canvas → reverse of the object array,
    // and never list the background frame (it's pinned).
    return c.getObjects().filter((o) => o !== bgRef.current).reverse();
  };
  const selectLayer = (obj: FabricObject) => {
    const c = fabricRef.current; if (!c) return;
    c.setActiveObject(obj); c.requestRenderAll();
  };
  const toggleLayerVisible = (obj: FabricObject) => {
    const c = fabricRef.current; if (!c) return;
    obj.visible = obj.visible === false ? true : false;
    if (!obj.visible && c.getActiveObject() === obj) c.discardActiveObject();
    c.requestRenderAll(); setLayersTick((n) => n + 1); saveState();
  };
  const moveLayer = (obj: FabricObject, dir: 'up' | 'down') => {
    const c = fabricRef.current; if (!c) return;
    if (dir === 'up') c.bringObjectForward(obj); else c.sendObjectBackwards(obj);
    if (bgRef.current && c.getObjects().includes(bgRef.current)) c.sendObjectToBack(bgRef.current);
    c.requestRenderAll(); setLayersTick((n) => n + 1); saveState();
  };
  const removeLayer = (obj: FabricObject) => {
    const c = fabricRef.current; if (!c) return;
    c.remove(obj); c.discardActiveObject(); c.requestRenderAll();
  };
  const layerLabel = (o: FabricObject): string => {
    if (o.type === 'i-text' || o.type === 'text') return (o as unknown as { text?: string }).text?.slice(0, 22) || 'Text';
    if (o.type === 'image') return t('designer.layerImage', 'Image');
    if (o.type === 'group') return t('designer.layerGraphic', 'Graphic');
    if (o.type === 'line') return t('designer.layerLine', 'Line');
    return o.type ? o.type.charAt(0).toUpperCase() + o.type.slice(1) : 'Layer';
  };

  /* ---------- Styles ---------- */

  // Bigger picker cell for the Shapes / Ribbons / Icons selection modals.
  const pickerCell = 'flex aspect-square items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-2 transition-colors hover:border-primary-400 hover:bg-primary-50';

  // Colour control rendered in each picker modal's toolbar — a swatch that
  // toggles an inline HexColorPicker. Everything inserted picks up this colour.
  const colorControl = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t('designer.insertColor', 'Color')}</span>
      <button
        type="button"
        onClick={() => setShowInlineColor((v) => !v)}
        className="h-6 w-6 rounded border border-gray-300 transition-shadow hover:ring-2 hover:ring-primary-200"
        style={{ backgroundColor: selectedColor }}
        title={selectedColor}
      />
      <span className="font-mono text-xs text-gray-400">{selectedColor}</span>
      {showInlineColor && (
        <div className="w-full pt-2">
          <HexColorPicker color={selectedColor} onChange={setSelectedColor} style={{ width: '100%', maxWidth: 240, height: 140 }} />
        </div>
      )}
    </div>
  );

  const iconQuery = iconSearch.trim().toLowerCase();
  const filteredIcons = iconQuery ? ICON_NAMES.filter((n) => n.includes(iconQuery)) : ICON_NAMES;

  // Shared category filter (by name) for the Shapes + Ribbons modals.
  const catQuery = catSearch.trim().toLowerCase();
  const visibleShapeCats = catQuery ? SHAPE_CATEGORIES.filter((c) => c.name.toLowerCase().includes(catQuery)) : SHAPE_CATEGORIES;
  const visibleRibbonCats = catQuery ? RIBBON_CATEGORIES.filter((c) => c.name.toLowerCase().includes(catQuery)) : RIBBON_CATEGORIES;
  const catSearchInput = (
    <input
      data-testid="cat-search"
      value={catSearch}
      onChange={(e) => setCatSearch(e.target.value)}
      placeholder={t('designer.searchCategory', 'Filter categories…')}
      className="ml-auto w-44 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
    />
  );

  /* ---------- 3-column render data ---------- */

  const iconStroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const RAIL: { id: PanelId; label: string; d: string }[] = [
    { id: 'templates', label: t('designer.panelTemplates', 'Templates'), d: 'M3 3h18v18H3zM3 9h18M9 21V9' },
    { id: 'uploads', label: t('designer.panelUploads', 'Uploads'), d: 'M3 3h18v18H3zM7.5 8.5h.01M21 15l-5-5L5 21' },
    { id: 'elements', label: t('designer.panelElements', 'Elements'), d: 'M6 3a3 3 0 100 6 3 3 0 000-6zM14 3h7v7h-7zM6 14v7M3 17h6' },
    { id: 'text', label: t('designer.panelText', 'Text'), d: 'M5 5h14M12 5v14M8 19h8' },
    { id: 'attributes', label: t('designer.panelAttributes', 'Attributes'), d: 'M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h2M16 4h2a2 2 0 012 2v12a2 2 0 01-2 2h-2' },
    { id: 'qr', label: t('designer.panelQr', 'QR Codes'), d: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h3v3M20 14v.01M14 20v.01M20 20v.01' },
    { id: 'layers', label: t('designer.panelLayers', 'Layers'), d: 'M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5' },
  ];

  const ATTRIBUTE_GROUPS: { group: string; tokens: { token: string; label: string }[] }[] = [
    { group: t('designer.attrRecipient', 'Recipient'), tokens: [
      { token: '{{recipient_name}}', label: t('designer.attrRecipientName', 'Recipient name') },
      { token: '{{recipient_email}}', label: t('designer.attrRecipientEmail', 'Recipient email') },
    ] },
    { group: t('designer.attrIssuer', 'Issuer'), tokens: [
      { token: '{{issuer_name}}', label: t('designer.attrIssuerName', 'Issuer name') },
    ] },
    { group: t('designer.attrBadge', 'Badge'), tokens: [
      { token: '{{badge_name}}', label: t('designer.attrBadgeName', 'Badge name') },
    ] },
    { group: t('designer.attrAward', 'Credential'), tokens: [
      { token: '{{date}}', label: t('designer.attrDate', 'Issue date') },
      { token: '{{award_url}}', label: t('designer.attrAwardUrl', 'Credential URL') },
    ] },
  ];

  // Ready-made text combinations for the Text panel.
  const COMBINATIONS: { key: string; preview: string[]; lines: { text: string; size: number; weight?: string }[] }[] = [
    { key: 'certificate', preview: ['CERTIFICATE', 'OF ACHIEVEMENT'], lines: [{ text: 'CERTIFICATE', size: 34, weight: '800' }, { text: 'OF ACHIEVEMENT', size: 13, weight: '600' }] },
    { key: 'completion', preview: ['CERTIFICATE OF COMPLETION', '{{recipient_name}}'], lines: [{ text: 'CERTIFICATE OF COMPLETION', size: 16, weight: '700' }, { text: '{{recipient_name}}', size: 24, weight: '600' }] },
    { key: 'signature', preview: ['Name Surname', 'Program Mentor'], lines: [{ text: 'Name Surname', size: 20, weight: '700' }, { text: 'Program Mentor', size: 13, weight: 'normal' }] },
    { key: 'issued', preview: ['{{date}}', 'Issue Date'], lines: [{ text: '{{date}}', size: 18, weight: '700' }, { text: 'Issue Date', size: 12, weight: 'normal' }] },
    { key: 'awarded', preview: ['AWARDED TO', '{{recipient_name}}'], lines: [{ text: 'AWARDED TO', size: 13, weight: '700' }, { text: '{{recipient_name}}', size: 28, weight: '800' }] },
    { key: 'verify', preview: ['Verify at', '{{award_url}}'], lines: [{ text: 'Verify at', size: 12, weight: '600' }, { text: '{{award_url}}', size: 12, weight: 'normal' }] },
  ];

  // Templates panel — same filter logic as the old modal.
  const tplHasFilter = !!(tplFilterStyle || tplFilterShape || tplFilterPalette);
  const tplVisible = TEMPLATES.filter((tpl) => {
    if (tplFilterStyle && tpl.style !== tplFilterStyle) return false;
    if (tplFilterShape && tpl.shape !== tplFilterShape) return false;
    if (tplFilterPalette && tpl.palette !== tplFilterPalette) return false;
    return true;
  });
  // Elements → Background shapes: layer-less frames, optionally narrowed by shape.
  const bgVisible = BACKGROUND_TEMPLATES.filter((tpl) => !bgShape || tpl.shape === bgShape);
  const tplStyleEntries = Object.entries(TEMPLATE_STYLES);

  const sectionLabel = 'mb-2 mt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400';
  const railBtn = 'inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600';

  // Contextual top toolbar — depends on the selected object's type.
  const isText = activeType === 'i-text' || activeType === 'text';
  const contextToolbar = hasSelection ? (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="context-toolbar">
      {isText && (
        <>
          <select data-testid="designer-font-select" value={selectedFont} onChange={(e) => applyFont(e.target.value)} className="max-w-[170px] rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700">
            {FONT_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.fonts.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
              </optgroup>
            ))}
            {customFonts.length > 0 && (
              <optgroup label={t('designer.customFonts', 'Custom')}>
                {customFonts.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
              </optgroup>
            )}
          </select>
          <div className="flex items-center overflow-hidden rounded-md border border-gray-200 bg-white" data-testid="font-size-control" title={t('designer.fontSize', 'Font size')}>
            <button type="button" data-testid="font-size-dec" onClick={() => changeFontSize((c) => c - 2)} className="px-2 py-1.5 text-base leading-none text-gray-600 hover:bg-primary-50 hover:text-primary-600" aria-label={t('designer.fontSizeDecrease', 'Decrease font size')}>&minus;</button>
            <input
              data-testid="font-size-input"
              type="number"
              min={6}
              max={200}
              value={selectedFontSize}
              onChange={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v)) changeFontSize(v); }}
              className="w-9 border-x border-gray-200 py-1 text-center text-sm text-gray-700 [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <button type="button" data-testid="font-size-inc" onClick={() => changeFontSize((c) => c + 2)} className="px-2 py-1.5 text-base leading-none text-gray-600 hover:bg-primary-50 hover:text-primary-600" aria-label={t('designer.fontSizeIncrease', 'Increase font size')}>+</button>
          </div>
          <label className="cursor-pointer whitespace-nowrap px-1 text-xs text-primary-600 hover:underline">
            {t('designer.uploadFont', '+ Font')}
            <input data-testid="upload-font-input" type="file" accept=".ttf,.woff,.woff2,.otf" onChange={handleFontUpload} className="hidden" />
          </label>
        </>
      )}
      <div className="relative" ref={colorPickerRef}>
        <button data-testid="open-color-picker" onClick={() => setShowColorPicker((v) => !v)} className={railBtn} title={t('designer.color', 'Color')}>
          <span className="inline-block h-4 w-4 rounded border border-gray-300" style={{ backgroundColor: selectedColor }} />
        </button>
        {showColorPicker && (
          <div className="absolute left-0 top-full z-30 mt-1 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
            <HexColorPicker color={selectedColor} onChange={applyColor} />
          </div>
        )}
      </div>
      <div className="mx-1 h-6 w-px bg-gray-200" />
      <button data-testid="align-left" onClick={() => alignH('left')} className={railBtn} title={t('designer.alignLeft', 'Align left')}><svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><rect x="2" y="3" width="2" height="18" /><rect x="6" y="6" width="14" height="3" /><rect x="6" y="15" width="9" height="3" /></svg></button>
      <button data-testid="align-center-h" onClick={() => alignH('center')} className={railBtn} title={t('designer.alignHCenter', 'Center H')}><svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><rect x="11" y="3" width="2" height="18" /><rect x="5" y="6" width="14" height="3" /><rect x="7.5" y="15" width="9" height="3" /></svg></button>
      <button data-testid="align-right" onClick={() => alignH('right')} className={railBtn} title={t('designer.alignRight', 'Align right')}><svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><rect x="20" y="3" width="2" height="18" /><rect x="4" y="6" width="14" height="3" /><rect x="9" y="15" width="9" height="3" /></svg></button>
      <button data-testid="align-top" onClick={() => alignV('top')} className={railBtn} title={t('designer.alignTop', 'Align top')}><svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><rect x="3" y="2" width="18" height="2" /><rect x="6" y="6" width="3" height="14" /><rect x="15" y="6" width="3" height="9" /></svg></button>
      <button data-testid="align-center-v" onClick={() => alignV('middle')} className={railBtn} title={t('designer.alignVMiddle', 'Center V')}><svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="2" /><rect x="6" y="5" width="3" height="14" /><rect x="15" y="7.5" width="3" height="9" /></svg></button>
      <button data-testid="align-bottom" onClick={() => alignV('bottom')} className={railBtn} title={t('designer.alignBottom', 'Align bottom')}><svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><rect x="3" y="20" width="18" height="2" /><rect x="6" y="4" width="3" height="14" /><rect x="15" y="9" width="3" height="9" /></svg></button>
      <div className="mx-1 h-6 w-px bg-gray-200" />
      <button data-testid="designer-delete" onClick={deleteSelected} className={railBtn} title={t('designer.delete', 'Delete')}><svg className="h-4 w-4 text-red-500" {...iconStroke} viewBox="0 0 24 24"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
    </div>
  ) : (
    <span className="text-xs text-gray-400">{t('designer.selectHint', 'Select a layer to edit it')}</span>
  );

  /* ---------- Render ---------- */

  const selectSm = 'rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400';
  const seeAll = 'text-xs font-medium text-primary-600 hover:underline';

  return (
    <div className={`flex h-full min-h-0 flex-col-reverse lg:flex-row ${className || ''}`}>
      {/* Rail — bottom tab bar on mobile, left rail on desktop */}
      <nav className="flex w-full flex-none flex-row overflow-x-auto border-t border-gray-200 bg-gray-50 lg:w-[74px] lg:flex-col lg:overflow-y-auto lg:border-t-0 lg:border-r lg:py-2">
        {RAIL.map((item) => (
          <button
            key={item.id}
            data-testid={`panel-${item.id}`}
            onClick={() => { setActivePanel(item.id); setElementsView(null); setMobilePanelOpen(true); }}
            className={`relative flex flex-none flex-col items-center gap-1 px-3 py-2.5 text-[10px] font-semibold transition-colors lg:px-1 ${activePanel === item.id ? 'text-primary-600' : 'text-gray-500 hover:text-gray-800'}`}
          >
            {activePanel === item.id && <span className="absolute bottom-0 left-1.5 right-1.5 top-auto h-[3px] rounded-t bg-primary-600 lg:left-0 lg:right-auto lg:top-1.5 lg:bottom-1.5 lg:h-auto lg:w-[3px] lg:rounded-r" />}
            <svg className="h-5 w-5" viewBox="0 0 24 24" {...iconStroke}><path d={item.d} /></svg>
            {item.label}
          </button>
        ))}
      </nav>

      {/* Panel — collapsible bottom sheet on mobile, left panel on desktop */}
      <div data-testid={`panel-body-${activePanel}`} className={`${mobilePanelOpen ? 'flex' : 'hidden'} max-h-[46vh] w-full flex-none flex-col overflow-y-auto border-t border-gray-200 bg-white p-4 lg:!flex lg:max-h-none lg:w-[320px] lg:border-t-0 lg:border-r`}>
        {/* Mobile-only sheet handle to collapse the panel and reveal the canvas */}
        <button onClick={() => setMobilePanelOpen(false)} className="mb-2 flex items-center justify-center gap-1 rounded-md py-1 text-xs font-medium text-gray-400 lg:hidden" aria-label="Collapse panel">
          <svg className="h-4 w-4" {...iconStroke} viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg>
        </button>
        {activePanel === 'templates' && (
          <div>
            {accountTemplates && accountTemplates.length > 0 && (
              <div className="mb-3 flex rounded-lg border border-gray-200 p-0.5 text-xs font-semibold">
                <button data-testid="tpl-source-library" onClick={() => setTplSource('library')} className={`flex-1 rounded-md py-1.5 ${tplSource === 'library' ? 'bg-primary-600 text-white' : 'text-gray-600'}`}>{t('designer.sourceLibrary', 'Library')}</button>
                <button data-testid="tpl-source-mine" onClick={() => setTplSource('mine')} className={`flex-1 rounded-md py-1.5 ${tplSource === 'mine' ? 'bg-primary-600 text-white' : 'text-gray-600'}`}>{t('designer.sourceMine', 'My Templates')}</button>
              </div>
            )}
            {tplSource === 'mine' && accountTemplates ? (
              <div className="grid grid-cols-2 gap-2">
                {accountTemplates.map((tpl) => (
                  <button key={tpl.id} data-testid={`my-template-card-${tpl.id}`} onClick={() => onUseAccountTemplate?.(tpl.id)} title={tpl.name}
                    className="group flex flex-col overflow-hidden rounded-lg border border-gray-200 transition-all hover:border-primary-400 hover:shadow">
                    <div className="flex aspect-square items-center justify-center bg-gray-50 p-2">{tpl.image ? <img src={tpl.image} alt={tpl.name} className="max-h-full max-w-full object-contain" loading="lazy" /> : <span className="text-[10px] text-gray-300">—</span>}</div>
                    <div className="truncate border-t border-gray-100 px-2 py-1 text-left text-[10px] font-semibold text-gray-600">{tpl.name}</div>
                  </button>
                ))}
                {accountTemplates.length === 0 && <div className="col-span-2 py-8 text-center text-xs text-gray-400">{t('designer.noMyTemplates', 'Your saved designs appear here.')}</div>}
              </div>
            ) : (
            <>
            <div className="mb-3 flex items-center gap-2">
              {tplStyleEntries.length > 0 && (
                <select data-testid="template-style-filter" className={selectSm} value={tplFilterStyle} onChange={(e) => setTplFilterStyle(e.target.value)}>
                  <option value="">{t('designer.filterCategory', 'Category')}</option>
                  {tplStyleEntries.map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
                </select>
              )}
              {TEMPLATES.some((tpl) => tpl.shape) && (
                <select data-testid="template-shape-filter" className={selectSm} value={tplFilterShape} onChange={(e) => setTplFilterShape(e.target.value)}>
                  <option value="">{t('designer.filterShape', 'Shape')}</option>
                  {Object.entries(TEMPLATE_SHAPES).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
                </select>
              )}
              {tplHasFilter && <button data-testid="template-clear" onClick={() => { setTplFilterStyle(''); setTplFilterShape(''); setTplFilterPalette(''); }} className="ml-auto text-xs text-primary-600 hover:underline">{t('designer.clearFilters', 'Clear')}</button>}
            </div>
            <button data-testid="template-none" onClick={() => handleTemplateSelect('')} className="mb-3 w-full rounded-lg border border-dashed border-gray-300 py-2 text-xs font-medium text-gray-500 hover:border-gray-400">{t('designer.noTemplate', 'Blank canvas')}</button>
            <div className="grid grid-cols-2 gap-2">
              {tplVisible.map((tpl) => (
                <button key={tpl.id} data-testid={`template-card-${tpl.id}`} onClick={() => handleTemplateSelect(tpl.id)} title={tpl.label || tpl.id}
                  className={`group relative flex flex-col overflow-hidden rounded-lg border transition-all hover:shadow ${tpl.id === selectedTemplate ? 'border-primary-400 ring-1 ring-primary-400' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="flex aspect-square items-center justify-center bg-gray-50 p-2"><img src={tpl.thumb} alt={tpl.label || tpl.id} className="max-h-full max-w-full object-contain" loading="lazy" /></div>
                  {tpl.style && <div className="truncate border-t border-gray-100 px-2 py-1 text-left text-[10px] font-semibold text-gray-600">{tpl.styleLabel} · {tpl.paletteLabel}</div>}
                </button>
              ))}
            </div>
            {tplVisible.length === 0 && <div className="py-8 text-center text-sm text-gray-400">{t('designer.noMatches', 'No templates match these filters.')}</div>}
            </>
            )}
          </div>
        )}

        {activePanel === 'uploads' && (
          <div>
            <label data-testid="upload-image" className="mb-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <svg className="h-4 w-4" {...iconStroke} viewBox="0 0 24 24"><path d="M12 16V4m0 0L8 8m4-4l4 4M4 20h16" /></svg>
              {t('designer.uploadImage', 'Upload image')}
              <input data-testid="upload-image-input" type="file" accept=".png,.jpg,.jpeg,.svg" onChange={handleUploadBg} className="hidden" />
            </label>
            <p className="mb-4 text-center text-xs text-gray-400">{t('designer.uploadHint', 'Supports SVG, PNG and JPG. Up to 5 MB.')}</p>
            <div className={sectionLabel}>{t('designer.allUsed', 'All used')}</div>
            <div className="grid grid-cols-2 gap-2">
              {uploads.map((u, i) => (
                <button key={i} data-testid={`upload-${i}`} onClick={() => insertUpload(u)} className="flex aspect-square items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-2 hover:border-primary-400"><img src={u} alt="" className="max-h-full max-w-full object-contain" /></button>
              ))}
              {uploads.length === 0 && <div className="col-span-2 py-6 text-center text-xs text-gray-400">{t('designer.noUploads', 'Your uploads appear here.')}</div>}
            </div>
          </div>
        )}

        {activePanel === 'elements' && elementsView === null && (
          <div className="space-y-5">
            {colorControl}
            <div>
              <div className={sectionLabel}>{t('designer.lines', 'Lines')}</div>
              <div className="grid grid-cols-4 gap-2">
                <button data-testid="line-h" onClick={() => addLine('h')} className={pickerCell} title="Horizontal line"><span className="block h-0.5 w-8 rounded" style={{ background: selectedColor }} /></button>
                <button data-testid="line-v" onClick={() => addLine('v')} className={pickerCell} title="Vertical line"><span className="block h-8 w-0.5 rounded" style={{ background: selectedColor }} /></button>
              </div>
            </div>
            <div>
              <div className={sectionLabel}>{t('designer.shapesGeneric', 'Shapes')}</div>
              <div className="grid grid-cols-4 gap-2">
                <button data-testid="shape-rect-fill" onClick={() => addGenericShape('rect', true)} className={pickerCell}><span className="h-7 w-7" style={{ background: selectedColor }} /></button>
                <button data-testid="shape-round-fill" onClick={() => addGenericShape('roundRect', true)} className={pickerCell}><span className="h-7 w-7 rounded-md" style={{ background: selectedColor }} /></button>
                <button data-testid="shape-circle-fill" onClick={() => addGenericShape('circle', true)} className={pickerCell}><span className="h-7 w-7 rounded-full" style={{ background: selectedColor }} /></button>
                <button data-testid="shape-triangle-fill" onClick={() => addGenericShape('triangle', true)} className={pickerCell}><span style={{ width: 0, height: 0, borderLeft: '14px solid transparent', borderRight: '14px solid transparent', borderBottom: `24px solid ${selectedColor}` }} /></button>
                <button data-testid="shape-rect-line" onClick={() => addGenericShape('rect', false)} className={pickerCell}><span className="h-7 w-7 border-2" style={{ borderColor: selectedColor }} /></button>
                <button data-testid="shape-round-line" onClick={() => addGenericShape('roundRect', false)} className={pickerCell}><span className="h-7 w-7 rounded-md border-2" style={{ borderColor: selectedColor }} /></button>
                <button data-testid="shape-circle-line" onClick={() => addGenericShape('circle', false)} className={pickerCell}><span className="h-7 w-7 rounded-full border-2" style={{ borderColor: selectedColor }} /></button>
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between"><span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t('designer.icons', 'Icons')}</span><button className={seeAll} onClick={() => setElementsView('icons')}>{t('designer.seeAll', 'See all')}</button></div>
              <div className="grid grid-cols-6 gap-1.5">{ICON_NAMES.slice(0, 12).map((name) => (<button key={name} data-testid={`icon-${name}`} onClick={() => handleIconSelect(name)} className={pickerCell} title={name}><span className="inline-block h-7 w-7" style={maskSwatch(`/assets/badge-icons/icon-${name}.svg`, selectedColor)} /></button>))}</div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between"><span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t('designer.ribbons', 'Ribbons')}</span><button className={seeAll} onClick={() => setElementsView('ribbons')}>{t('designer.seeAll', 'See all')}</button></div>
              <div className="grid grid-cols-2 gap-2">{[1, 2, 3, 4].map((n) => (<button key={n} data-testid={`ribbon-${n}`} onClick={() => handleRibbonSelect(n)} className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-2 py-3 hover:border-primary-400"><span className="inline-block h-7 w-full" style={maskSwatch(`/assets/badge-ribbons/ribbon-${padNum(n)}.svg`, selectedColor)} /></button>))}</div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between"><span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t('designer.bases', 'Bases')}</span><button className={seeAll} onClick={() => setElementsView('bases')}>{t('designer.seeAll', 'See all')}</button></div>
              <div className="grid grid-cols-4 gap-2">{[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (<button key={n} data-testid={`base-${n}`} onClick={() => handleShapeSelect(n)} className={pickerCell} title={`Base ${n}`}><span className="inline-block h-8 w-8" style={maskSwatch(`/assets/badge-shapes/shape-${padNum(n)}.svg`, selectedColor)} /></button>))}</div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between"><span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t('designer.backgrounds', 'Background shapes')}</span><button data-testid="backgrounds-see-all" className={seeAll} onClick={() => setElementsView('backgrounds')}>{t('designer.seeAll', 'See all')}</button></div>
              <div className="grid grid-cols-4 gap-2">{BACKGROUND_TEMPLATES.slice(0, 8).map((tpl) => (<button key={tpl.id} data-testid={`background-card-${tpl.id}`} onClick={() => handleTemplateSelect(tpl.id)} className={pickerCell} title={tpl.label || tpl.id}><img src={tpl.thumb} alt="" className="max-h-full max-w-full object-contain" loading="lazy" /></button>))}</div>
            </div>
          </div>
        )}
        {activePanel === 'elements' && elementsView === 'icons' && (
          <div>
            <button onClick={() => setElementsView(null)} className="mb-2 text-xs text-primary-600 hover:underline">‹ {t('designer.back', 'Back')}</button>
            {colorControl}
            <input data-testid="icon-search" value={iconSearch} onChange={(e) => setIconSearch(e.target.value)} placeholder={t('designer.searchIcons', 'Search icons…')} className="my-2 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm" />
            <div className="grid grid-cols-5 gap-1.5">{filteredIcons.map((name) => (<button key={name} data-testid={`icon-${name}`} onClick={() => handleIconSelect(name)} className={pickerCell} title={name}><span className="inline-block h-8 w-8" style={maskSwatch(`/assets/badge-icons/icon-${name}.svg`, selectedColor)} /></button>))}</div>
          </div>
        )}
        {activePanel === 'elements' && elementsView === 'ribbons' && (
          <div>
            <button onClick={() => setElementsView(null)} className="mb-2 text-xs text-primary-600 hover:underline">‹ {t('designer.back', 'Back')}</button>
            {colorControl}
            {visibleRibbonCats.map((cat) => (
              <div key={cat.name} className="mt-3">
                <div className={sectionLabel}>{cat.name}</div>
                <div className="grid grid-cols-2 gap-2">{Array.from({ length: cat.range[1] - cat.range[0] + 1 }, (_, i) => cat.range[0] + i).map((n) => (<button key={n} data-testid={`ribbon-${n}`} onClick={() => handleRibbonSelect(n)} className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-2 py-3 hover:border-primary-400"><span className="inline-block h-7 w-full" style={maskSwatch(`/assets/badge-ribbons/ribbon-${padNum(n)}.svg`, selectedColor)} /></button>))}</div>
              </div>
            ))}
          </div>
        )}
        {activePanel === 'elements' && elementsView === 'bases' && (
          <div>
            <button onClick={() => setElementsView(null)} className="mb-2 text-xs text-primary-600 hover:underline">‹ {t('designer.back', 'Back')}</button>
            {colorControl}
            {catSearchInput}
            {visibleShapeCats.map((cat) => (
              <div key={cat.name} className="mt-3">
                <div className={sectionLabel}>{cat.name}</div>
                <div className="grid grid-cols-4 gap-2">{Array.from({ length: cat.range[1] - cat.range[0] + 1 }, (_, i) => cat.range[0] + i).map((n) => (<button key={n} data-testid={`base-${n}`} onClick={() => handleShapeSelect(n)} className={pickerCell} title={`Base ${n}`}><span className="inline-block h-9 w-9" style={maskSwatch(`/assets/badge-shapes/shape-${padNum(n)}.svg`, selectedColor)} /></button>))}</div>
              </div>
            ))}
          </div>
        )}
        {activePanel === 'elements' && elementsView === 'backgrounds' && (
          <div>
            <button onClick={() => setElementsView(null)} className="mb-2 text-xs text-primary-600 hover:underline">‹ {t('designer.back', 'Back')}</button>
            <p className="mb-2 text-xs text-gray-400">{t('designer.backgroundsHint', 'A pre-designed frame applied as the badge background — your text and elements stay on top.')}</p>
            <div className="mb-2 flex items-center gap-2">
              <select data-testid="background-shape-filter" className={selectSm} value={bgShape} onChange={(e) => setBgShape(e.target.value)}>
                <option value="">{t('designer.filterShape', 'Shape')}</option>
                {Object.entries(TEMPLATE_SHAPES).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
              </select>
              {bgShape && <button data-testid="background-clear" onClick={() => setBgShape('')} className="ml-auto text-xs text-primary-600 hover:underline">{t('designer.clearFilters', 'Clear')}</button>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {bgVisible.map((tpl) => (
                <button key={tpl.id} data-testid={`background-card-${tpl.id}`} onClick={() => handleTemplateSelect(tpl.id)} title={tpl.label || tpl.id}
                  className="group flex flex-col overflow-hidden rounded-lg border border-gray-200 transition-all hover:border-primary-400 hover:shadow">
                  <div className="flex aspect-square items-center justify-center bg-gray-50 p-2"><img src={tpl.thumb} alt={tpl.label || tpl.id} className="max-h-full max-w-full object-contain" loading="lazy" /></div>
                </button>
              ))}
            </div>
            {bgVisible.length === 0 && <div className="py-8 text-center text-sm text-gray-400">{t('designer.noMatches', 'No backgrounds match these filters.')}</div>}
          </div>
        )}

        {activePanel === 'text' && (
          <div className="space-y-4">
            <button data-testid="add-text" onClick={() => addNormalText()} className="w-full rounded-lg border border-primary-300 bg-primary-50 px-3 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-100">+ {t('designer.addTextBox', 'Add text box')}</button>
            <div>
              <div className={sectionLabel}>{t('designer.textStyles', 'Text styles')}</div>
              <div className="space-y-2">
                <button data-testid="add-heading" onClick={addHeading} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-3 text-center text-xl font-extrabold text-gray-900 hover:border-primary-400">{t('designer.addHeading', 'Add heading')}</button>
                <button data-testid="add-subheading" onClick={addSubheading} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-center text-base font-semibold text-gray-800 hover:border-primary-400">{t('designer.addSubheading', 'Add subheading')}</button>
                <button data-testid="add-normal-text" onClick={addNormalText} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-center text-sm text-gray-700 hover:border-primary-400">{t('designer.addNormalText', 'Add normal text')}</button>
              </div>
            </div>
            <div>
              <div className={sectionLabel}>{t('designer.combinations', 'Combinations')}</div>
              <div className="grid grid-cols-2 gap-2">
                {COMBINATIONS.map((combo) => (
                  <button key={combo.key} data-testid={`combo-${combo.key}`} onClick={() => addCombination(combo.lines)} className="flex flex-col items-center justify-center gap-0.5 rounded-lg border border-gray-200 bg-white px-2 py-4 text-center hover:border-primary-400">
                    {combo.preview.map((line, i) => (
                      <span key={i} className={i === 0 ? 'text-xs font-bold text-gray-800' : 'text-[10px] text-gray-500'}>{line}</span>
                    ))}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {activePanel === 'attributes' && (
          <div className="space-y-4">
            <p className="text-xs text-gray-500">{t('designer.attributesHint', 'Insert a token — it fills in per recipient when the badge is issued.')}</p>
            {/* Steer per-recipient personalization to the certificate: under the
                Open Badges standard the badge image is a shared class emblem. */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-800">
                <span aria-hidden className="mt-px flex-none">⚠️</span>
                <span>{t('designer.attributesCertWarning', 'Tokens personalize the badge IMAGE for each recipient. Under the Open Badges 2.0 standard, a badge (BadgeClass) image is a shared, class-level emblem — the recipient’s name belongs on their certificate, not the badge. For per-recipient names, use the Certificate designer instead.')}</span>
              </p>
              <a href={localizedDocsHref('/docs/guide/certificates', i18n.language)} target="_blank" rel="noopener noreferrer"
                className="mt-1.5 ml-5 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 underline hover:text-amber-700">
                {t('designer.attributesCertLink', 'How certificates work')}
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Z" clipRule="evenodd" /><path fillRule="evenodd" d="M6.194 12.753a.75.75 0 0 0 1.06.053L16.5 4.44v2.81a.75.75 0 0 0 1.5 0v-4.5a.75.75 0 0 0-.75-.75h-4.5a.75.75 0 0 0 0 1.5h2.553l-9.056 8.194a.75.75 0 0 0-.053 1.06Z" clipRule="evenodd" /></svg>
              </a>
            </div>
            {hasTokens && (
              <button
                onClick={handlePreview}
                disabled={previewLoading}
                data-testid="preview-tokens"
                className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-60">
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" /><path fillRule="evenodd" d="M.664 10.59a1.65 1.65 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clipRule="evenodd" /></svg>
                {previewLoading ? t('designer.previewLoading', 'Rendering…') : t('designer.previewTokens', 'Preview with sample data')}
              </button>
            )}
            {ATTRIBUTE_GROUPS.map((g) => (
              <div key={g.group}>
                <div className={sectionLabel}>{g.group}</div>
                <div className="space-y-1.5">
                  {g.tokens.map((tk) => (
                    <button key={tk.token} data-testid={`attr-${tk.token.replace(/[{}]/g, '')}`} onClick={() => insertToken(tk.token)} className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left hover:border-primary-400">
                      <span className="text-sm text-gray-700">{tk.label}</span>
                      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-primary-600">{tk.token}</code>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activePanel === 'qr' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">{t('designer.qrIntro', "Add a QR code linking to each credential's public verification page.")}</p>
            <button data-testid="add-qr" onClick={addQr} className="w-full rounded-lg bg-primary-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-primary-700">{t('designer.addQr', 'Add QR code')}</button>
            <p className="text-xs text-gray-400">{t('designer.qrNote', 'The scannable code is generated per recipient at issue time.')}</p>
          </div>
        )}

        {activePanel === 'layers' && (
          <div>
            <div className={sectionLabel}>{t('designer.layers', 'Layers')}</div>
            <div className="space-y-1.5">
              {getLayers().map((o, i) => (
                <div key={i} data-testid={`layer-${i}`} className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 ${fabricRef.current?.getActiveObject() === o ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-white'}`}>
                  <button onClick={() => selectLayer(o)} className="flex-1 truncate text-left text-sm text-gray-700">{layerLabel(o)}</button>
                  <button data-testid={`layer-up-${i}`} onClick={() => moveLayer(o, 'up')} title={t('designer.bringForward', 'Bring forward')} className="px-1 text-gray-400 hover:text-gray-700">▲</button>
                  <button data-testid={`layer-down-${i}`} onClick={() => moveLayer(o, 'down')} title={t('designer.sendBack', 'Send back')} className="px-1 text-gray-400 hover:text-gray-700">▼</button>
                  <button data-testid={`layer-vis-${i}`} onClick={() => toggleLayerVisible(o)} title={t('designer.toggleVisible', 'Show / hide')} className="px-1 text-gray-400 hover:text-gray-700">{o.visible === false ? '⊘' : '◉'}</button>
                  <button data-testid={`layer-del-${i}`} onClick={() => removeLayer(o)} title={t('designer.delete', 'Delete')} className="px-1 text-red-400 hover:text-red-600">✕</button>
                </div>
              ))}
              {getLayers().length === 0 && <div className="py-6 text-center text-xs text-gray-400">{t('designer.noLayers', 'Add elements to see layers here.')}</div>}
            </div>
          </div>
        )}
      </div>

      {/* Col 3 — canvas (fills the top on mobile) */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-gray-50">
        <div className="flex min-h-[52px] items-center gap-1.5 overflow-x-auto border-b border-gray-200 bg-white px-3 py-2">
          <button data-testid="designer-undo" onClick={undo} disabled={!canUndo} className={`${railBtn} flex-none`} title={t('designer.undo', 'Undo')}><svg className="h-4 w-4" {...iconStroke} viewBox="0 0 24 24"><path d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" /></svg></button>
          <button data-testid="designer-redo" onClick={redo} disabled={!canRedo} className={`${railBtn} flex-none`} title={t('designer.redo', 'Redo')}><svg className="h-4 w-4" {...iconStroke} viewBox="0 0 24 24"><path d="M21 10H11a5 5 0 00-5 5v2M21 10l-4-4M21 10l-4 4" /></svg></button>
          <div className="mx-1 h-6 w-px flex-none bg-gray-200" />
          {contextToolbar}
          <div className="flex-1" />
          {topBarRight}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div ref={roomAreaRef} data-testid="designer-canvas-area" className="flex items-start justify-center px-4 pt-4" style={{ minHeight: 340 }}>
            <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg" style={{ width: roomSize, height: roomSize }}>
              <canvas ref={canvasElRef} />
            </div>
          </div>
          {detailsSlot && <div className="mt-4 border-t border-gray-200 bg-white">{detailsSlot}</div>}
        </div>
      </div>

      {previewImage !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setPreviewImage(null)}>
          <div className="fixed inset-0 bg-black/50" />
          <div className="relative z-10 w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">{t('designer.previewTitle', 'Preview — how it looks when issued')}</h3>
              <button onClick={() => setPreviewImage(null)} className="text-xl leading-none text-gray-400 hover:text-gray-600">&times;</button>
            </div>
            {previewLoading ? (
              <div className="flex h-64 items-center justify-center text-sm text-gray-400">{t('designer.previewLoading', 'Rendering…')}</div>
            ) : previewTooLarge ? (
              <div className="flex h-64 items-center justify-center px-6 text-center text-sm text-amber-600">{t('designer.previewTooLarge', 'This design is too large to preview — try using a smaller image, or fewer images.')}</div>
            ) : previewError ? (
              <div className="flex h-64 items-center justify-center text-sm text-red-500">{t('designer.previewError', 'Could not render the preview. Try again.')}</div>
            ) : previewImage ? (
              <img src={previewImage} alt="preview" className="mx-auto max-h-[60vh] w-auto rounded-lg border border-gray-100" />
            ) : null}
            <p className="mt-3 text-center text-xs text-gray-500">{t('designer.previewNote', 'Sample values shown (e.g. “Alex Morgan”). Each recipient gets their own — rendered by the same code used when issuing.')}</p>
          </div>
        </div>
      )}
    </div>
  );
});
